import { SustainTeamScheduleEntry } from "../../../store/thunks/ticketingPortalThunk";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface TeamMember {
  id: string;
  name: string;
  initials: string;
  email: string;
  role: "lead" | "member";
  /** Reporting manager's email, if assigned. */
  managerEmail?: string | null;
  /** Sustain Team the member belongs to (TKT_Teams.id); read-only from this endpoint. */
  teamId?: number | null;
  channelsOnDuty: { nav: boolean; email: boolean; teams: boolean };
  channelsOffDuty: { nav: boolean; email: boolean; teams: boolean };
  scope: "assigned" | "all" | "custom";
  avatarColor: string;
  startHour: string;
  endHour: string;
  /** Working status as returned by the API (e.g. "On Duty" / "Off Duty") */
  workingStatus: string;
  triggers?: { newTicket: boolean; assigned: boolean; unassigned: boolean; comment: boolean; agingTicket: boolean };
  /** Notification triggers when the member is On Duty (as returned by the API). */
  triggersOnDuty?: { newTicket: boolean; assigned: boolean; unassigned: boolean; comment: boolean; agingTicket: boolean };
  /** Notification triggers when the member is Off Duty (as returned by the API). */
  triggersOffDuty?: { newTicket: boolean; assigned: boolean; unassigned: boolean; comment: boolean; agingTicket: boolean };
  /** Selected urgency levels when scope is "custom" (as returned by the API). */
  customUrgencies?: string[];
  workingDays: {
    baseDays: DayKey[];
    overrides: Record<string, boolean>;
  };
}

export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export interface CalendarDay {
  dateObj: Date;
  date: number;
  month: string;
  dayKey: DayKey;
  dayLabel: string;
  isWeekend: boolean;
  isToday: boolean;
  weekStart: boolean;
}

export type ActiveView = "team" | "calendar";
export type SortDirection = "asc" | "desc";

export interface EditFormState {
  role: "lead" | "member";
  /** Reporting manager's email; empty string means no manager assigned. */
  managerEmail: string;
  /** Sustain Team (TKT_Teams.id) the member is assigned to; null means unassigned. */
  teamId: number | null;
  /** Controls which channel set is currently displayed in the panel. */
  onDuty: boolean;
  /** Notification channels when the member is On Duty (default: all on). */
  channelsOnDuty: { nav: boolean; email: boolean; teams: boolean };
  /** Notification channels when the member is Off Duty (default: email only). */
  channelsOffDuty: { nav: boolean; email: boolean; teams: boolean };
  triggers: { newTicket: boolean; assigned: boolean; unassigned: boolean; comment: boolean; agingTicket: boolean };
  scope: "all" | "assigned" | "custom";
  /** Notification triggers when the member is On Duty (default: all on). */
  triggersOnDuty: { newTicket: boolean; assigned: boolean; unassigned: boolean; comment: boolean; agingTicket: boolean };
  /** Notification triggers when the member is Off Duty (default: assigned only). */
  triggersOffDuty: { newTicket: boolean; assigned: boolean; unassigned: boolean; comment: boolean; agingTicket: boolean };
  /** Selected urgency levels when scope is "custom". */
  customUrgencies: string[];
}

export type Props = {
  onNavigate: (screen: string) => void;
};

// ─── Constants ────────────────────────────────────────────────────────────────
export const SCOPE_LABEL: Record<string, string> = {
  assigned: "Assigned Only",
  all:      "All Tickets",
  custom:   "Custom",
};

export const DAY_KEYS: DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_LABELS: Record<DayKey, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

export const MEMBER_SPECIALTY: Record<string, string> = {
  brooks:    "Systems Analyst",
  chen:      "Lead - Systems",
  johnson:   "Access & Permissions",
  kapoor:    "Process & Training",
  kim:       "Data Integrations",
  mendez:    "Analytics & Reporting",
  moreno:    "Field Operations",
  nguyen:    "Lead - Operations",
  park:      "Integration Support",
  rodriguez: "System Monitoring",
  shah:      "Quality Assurance",
  williams:  "User Support",
};

export const MOCK_TICKET_COUNTS: Record<string, number> = {
  brooks:    5,
  chen:      7,
  johnson:   4,
  kapoor:    3,
  kim:       6,
  mendez:    2,
  moreno:    4,
  nguyen:    8,
  park:      5,
  rodriguez: 3,
  shah:      4,
  williams:  2,
};

// ─── In-column roster filters (multi-select, GET /members query params) ──────
export interface FilterOption { value: string; label: string; color?: string; }

export const TEAM_COLORS = ["#005CBC", "#0077B6", "#7C4DFF", "#00A389", "#F2994A", "#EB5757"];

export interface RosterFilters {
  role: string[];
  manager: string[];
  team: string[];
  notificationChannel: string[];
  notificationScope: string[];
  workingStatus: string[];
}

export const EMPTY_ROSTER_FILTERS: RosterFilters = {
  role: [],
  manager: [],
  team: [],
  notificationChannel: [],
  notificationScope: [],
  workingStatus: [],
};

export const ROLE_FILTER_OPTIONS: FilterOption[] = [
  { value: "Lead",   label: "Lead" },
  { value: "Member", label: "Member" },
];

export const NOTIFICATION_CHANNEL_FILTER_OPTIONS: FilterOption[] = [
  { value: "IN_APP", label: "Navigator In-App" },
  { value: "EMAIL",  label: "Email" },
  { value: "TEAMS",  label: "Microsoft Teams" },
];

export const NOTIFICATION_SCOPE_FILTER_OPTIONS: FilterOption[] = [
  { value: "ALL_TICKETS",   label: "All Tickets" },
  { value: "ASSIGNED_ONLY", label: "Assigned Only" },
  { value: "CUSTOM",        label: "Custom" },
];

export const WORKING_STATUS_FILTER_OPTIONS: FilterOption[] = [
  { value: "On Duty",  label: "On Duty" },
  { value: "Off Duty", label: "Off Duty" },
];

// ─── Utility functions ────────────────────────────────────────────────────────
export const toDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const applyScheduleToMembers = (
  members: TeamMember[],
  scheduleEntries: SustainTeamScheduleEntry[],
): TeamMember[] =>
  members.map((member) => {
    const entry = scheduleEntries.find(
      (e) => (e.memberEmail ?? "").toLowerCase() === (member.email ?? "").toLowerCase(),
    );
    if (!entry) return member;
    return {
      ...member,
      startHour: entry.shiftStartCst?.slice(0, 5) || member.startHour,
      endHour:   entry.shiftEndCst?.slice(0, 5)   || member.endHour,
    };
  });

export const buildScheduleJson = (
  emailKey: string,
  calendarDays: CalendarDay[],
  calendarScheduleMap: Record<string, Record<string, boolean>>,
  calendarToggles: Record<string, Record<string, boolean>>,
): Record<string, Record<string, { is_working: boolean }>> => {
  const result: Record<string, Record<string, { is_working: boolean }>> = {};
  let weekKey = "";
  calendarDays.forEach((day) => {
    const dateKey = toDateKey(day.dateObj);
    if (day.dayKey === "mon") {
      weekKey = dateKey;
      result[weekKey] = {};
    }
    const apiChecked = (calendarScheduleMap[emailKey]?.[dateKey]) ?? false;
    const is_working =
      calendarToggles[emailKey] !== undefined && dateKey in calendarToggles[emailKey]
        ? calendarToggles[emailKey][dateKey]
        : apiChecked;
    if (weekKey) {
      result[weekKey][day.dayLabel] = { is_working };
    }
  });
  return result;
};

export const parseTimeToMinutes = (value: string) => {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

export const isValidTimeValue = (value: string) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);

export const getCstParts = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const pick = (type: string) => parts.find((pt) => pt.type === type)?.value || "0";
  const weekday = pick("weekday").slice(0, 3).toLowerCase();
  const dayKey = (
    weekday === "mon" || weekday === "tue" || weekday === "wed" ||
    weekday === "thu" || weekday === "fri" || weekday === "sat" || weekday === "sun"
  ) ? (weekday as DayKey) : "mon";

  return {
    year:   Number(pick("year")),
    month:  Number(pick("month")),
    day:    Number(pick("day")),
    hour:   Number(pick("hour")),
    minute: Number(pick("minute")),
    dayKey,
  };
};

export const getMondayOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const buildCalendarDays = (anchorDate: Date): CalendarDay[] => {
  const start = getMondayOfWeek(anchorDate);
  const days: CalendarDay[] = [];
  const todayKey = toDateKey(anchorDate);

  for (let i = 0; i < 28; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dayIdx = d.getDay();
    const dayKey = DAY_KEYS[(dayIdx + 6) % 7];
    days.push({
      dateObj: d,
      date: d.getDate(),
      month: d.toLocaleString("en-US", { month: "short" }),
      dayKey,
      dayLabel: DAY_LABELS[dayKey],
      isWeekend: dayKey === "sat" || dayKey === "sun",
      isToday: toDateKey(d) === todayKey,
      weekStart: i > 0 && dayKey === "mon",
    });
  }

  return days;
};

export const isWorkingOnDate = (member: TeamMember, date: Date, dayKey: DayKey) => {
  const key = toDateKey(date);
  if (Object.prototype.hasOwnProperty.call(member.workingDays.overrides, key)) {
    return member.workingDays.overrides[key];
  }
  return member.workingDays.baseDays.includes(dayKey);
};

export const isOnDutyNow = (member: TeamMember) => {
  const cst = getCstParts();
  const todayDate = new Date(cst.year, cst.month - 1, cst.day);
  const workingToday = isWorkingOnDate(member, todayDate, cst.dayKey);
  if (!workingToday) return false;
  const nowMin = cst.hour * 60 + cst.minute;
  const startMin = parseTimeToMinutes(member.startHour);
  const endMin = parseTimeToMinutes(member.endHour);
  if (startMin == null || endMin == null) return false;
  return nowMin >= startMin && nowMin < endMin;
};

export const isWorkingToday = (member: TeamMember) => {
  const cst = getCstParts();
  const todayDate = new Date(cst.year, cst.month - 1, cst.day);
  return isWorkingOnDate(member, todayDate, cst.dayKey);
};
